import { Loader2Icon } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  type AlertDialogProps,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/modules/ui/components/alert-dialog/AlertDialog';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';

export interface ConfirmDialogProps extends AlertDialogProps {
  title: string;
  description: React.ReactNode;
  trigger?: React.ReactNode;
  confirmText?: string;
  /** Replaces `confirmText` while `confirming` is true. */
  confirmingText?: string;
  cancelText?: string;
  variant?: 'primary' | 'destructive';
  /**
   * Set it for slow confirmations: the dialog then stays open and busy until the caller flips `open`, so the
   * work is visible instead of the dialog vanishing into a frozen page.
   */
  confirming?: boolean;
  /** A failed confirmation, shown in the dialog so the user can retry or cancel. Pair it with `confirming`. */
  error?: string | null;
  onCancel?: () => void;
  onConfirm: () => void;
}

export const ConfirmDialog = ({
  title,
  description,
  trigger,
  confirmText = 'Confirm',
  confirmingText,
  cancelText = 'Cancel',
  variant = 'primary',
  confirming,
  error,
  onCancel,
  onConfirm,
  ...props
}: ConfirmDialogProps) => {
  // Only a caller that tracks the confirmation owns closing; otherwise Radix closes on confirm as before.
  const callerClosesDialog = confirming !== undefined;
  const busy = confirming === true;

  return (
    <AlertDialog {...props}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}

      <AlertDialogContent
        onEscapeKeyDown={(event) => {
          if (busy) event.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {error && <InlineError>{error}</InlineError>}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={busy}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            variant={variant}
            disabled={busy}
            aria-busy={busy}
            onClick={(event) => {
              if (callerClosesDialog) event.preventDefault();
              if (busy) return;
              onConfirm();
            }}
          >
            {busy && <Loader2Icon className="animate-spin" />}
            {busy ? (confirmingText ?? confirmText) : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
